import type { ChangeEvent } from 'react';
import { useCallback, useId, useMemo } from 'react';

import {
  Box,
  ButtonBase,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { DateRangeIcon } from '@mui/x-date-pickers';
import { useNavigate } from 'react-router';

import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { MIN_DATE } from '@/features/DataHub/modules/Export/constants/dateSettings';
import { useExportSettingsActions } from '@/features/DataHub/modules/Export/hooks/useExportSettingsActions';
import {
  DateRangeType,
  type ExportSettingsType,
  ExportTypes
} from '@/features/DataHub/modules/Export/types/context.types';
import {
  getDateRangeValidation,
  sanitizePeriodNumber
} from '@/features/DataHub/modules/Export/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

import { DateRangePickers } from './DateRangePickers';
import { RangePeriodFields } from './RangePeriodFields';

import type { SelectChangeEvent } from '@mui/material/Select';

const DateSettings = () => {
  const navigate = useNavigate();
  const radioGroupId = useId();

  const { exportSettings, updateExportSetting } = useExportSettingsActions();

  const exportType = useAppSelector(state => state.export.type);

  const { dateRangeType, from, to, period, periodNumber } = exportSettings;

  const { timezoneLabel, dateFormatValue } = useDataHubUserConfig();

  const onChangeDateRangeType = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateExportSetting('dateRangeType', e.target.value as DateRangeType);
    },
    [updateExportSetting]
  );

  const onChangePeriodNumber = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateExportSetting('periodNumber', sanitizePeriodNumber(e.target.value));
    },
    [updateExportSetting]
  );

  const onChangePeriod = useCallback(
    (e: SelectChangeEvent<ExportSettingsType['period']>) => {
      updateExportSetting('period', e.target.value);
    },
    [updateExportSetting]
  );

  const onChangeDateRange = useCallback(
    (field: 'from' | 'to', value: Date | null) => {
      updateExportSetting(field, value);
    },
    [updateExportSetting]
  );

  const validation = useMemo(
    () =>
      getDateRangeValidation({
        dateRangeType,
        from,
        to,
        periodNumber
      }),
    [dateRangeType, from, to, periodNumber]
  );

  const dispatch = useAppDispatch();

  if (
    exportSettings.exportType === ExportTypes.IMAGE &&
    (exportType === 'seasonal' || exportType === 'forwardSimple')
  ) {
    return null;
  }
  return (
    <DataHubCard
      expandable
      icon={
        <DateRangeIcon
          sx={{
            fontSize: '16px'
          }}
        />
      }
      subtitle='Select date range and timezone options'
      title='Date settings'
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px',
          my: 1.5
        }}
      >
        <Divider sx={{ mb: 2 }} />
        <Typography sx={{ mb: 1.5, fontSize: 14 }}>Date settings</Typography>

        <FormControl>
          <RadioGroup
            aria-labelledby={radioGroupId}
            value={dateRangeType}
            onChange={onChangeDateRangeType}
          >
            <FormControlLabel
              control={<Radio />}
              label='All'
              value={DateRangeType.ALL}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 50,
                marginBottom: validation.periodNumberError ? 2 : 0
              }}
            >
              <FormControlLabel
                control={<Radio />}
                label='Last'
                value={DateRangeType.RANGE}
              />
              <RangePeriodFields
                period={period}
                periodNumber={periodNumber}
                periodNumberError={validation.periodNumberError}
                onChangePeriod={onChangePeriod}
                onChangePeriodNumber={onChangePeriodNumber}
              />
            </Box>
            <FormControlLabel
              control={<Radio />}
              label='From'
              value={DateRangeType.DATES}
            />
            <DateRangePickers
              dateFormat={dateFormatValue}
              from={from}
              fromError={validation.fromError}
              fromErrorMessage={validation.fromErrorMessage}
              maxDate={new Date()}
              minDate={MIN_DATE}
              to={to}
              toError={validation.toError}
              toErrorMessage={validation.toErrorMessage}
              onChange={onChangeDateRange}
            />
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}
        >
          Timezone: {timezoneLabel}{' '}
          <ButtonBase
            sx={{
              ml: 2,
              color: 'primary.main'
            }}
            onClick={() => {
              navigate('/profile');
              dispatch(closeDrawer());
            }}
          >
            Change in Settings
          </ButtonBase>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>
    </DataHubCard>
  );
};

export default DateSettings;
